import {
  MIN_SEARCH_QUERY_LENGTH,
  SEARCH_DEBOUNCE_MS,
  type TierPickState,
} from "@/components/search/globalSearchConstants";
import { SkillLevelFilterToolbar } from "@/components/search/skillLevelFilter";
import { SearchInputField } from "@/components/ui/SearchInputField";
import { useEffectiveMinStatus } from "@/hooks/useDraftViewer";
import { useAuth } from "@/lib/AuthContext";
import { logger } from "@/lib/logger";
import { useIsMobile } from "@/lib/useDeviceType";
import {
  catalogRowToCourse,
  searchCourseCatalog,
} from "@/supabase/services/courseCatalogSearch";
import type { Course } from "@/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { YStack } from "tamagui";
import {
  courseMatchesInstruments,
  courseMatchesSkillTiers,
  type InstrumentPickState,
} from "@/components/courses/courseCatalogFilters";

export type CoursesHubSearchState = {
  query: string;
  results: Course[];
};

type CoursesHubSearchProps = {
  tierPick: TierPickState;
  onTierPickChange: React.Dispatch<React.SetStateAction<TierPickState>>;
  instrumentPick: InstrumentPickState;
  onInstrumentPickChange: React.Dispatch<React.SetStateAction<InstrumentPickState>>;
  onQueryChange?: (query: string) => void;
  onResultsChange?: (state: CoursesHubSearchState | null) => void;
  autoFocus?: boolean;
};

const CATALOG_SEARCH_LIMIT = 100;

export const CoursesHubSearch = React.memo(function CoursesHubSearch({
  tierPick,
  onTierPickChange,
  instrumentPick,
  onInstrumentPickChange,
  onQueryChange,
  onResultsChange,
  autoFocus = false,
}: CoursesHubSearchProps) {
  const { user } = useAuth();
  const minStatus = useEffectiveMinStatus();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const [skillMenuOpen, setSkillMenuOpen] = useState(false);
  const [matchedCourses, setMatchedCourses] = useState<Course[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const trimmedQuery = query.trim();
  const isActive = trimmedQuery.length > 0;
  const canSearch = trimmedQuery.length >= MIN_SEARCH_QUERY_LENGTH;

  useEffect(() => {
    if (!canSearch) {
      setMatchedCourses([]);
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();
    setIsSearching(true);

    const handle = setTimeout(() => {
      void (async () => {
        try {
          // Skill levels + instruments are filtered client-side (see `results`),
          // so changing a filter never re-issues the search RPC.
          const rows = await searchCourseCatalog(
            trimmedQuery,
            CATALOG_SEARCH_LIMIT,
            user?.id,
            null,
            minStatus,
          );

          if (controller.signal.aborted) return;

          setMatchedCourses(rows.map(catalogRowToCourse));
        } catch (err) {
          if (controller.signal.aborted) return;
          logger.error("Course catalog search failed", err);
          setMatchedCourses([]);
        } finally {
          if (!controller.signal.aborted) {
            setIsSearching(false);
          }
        }
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [trimmedQuery, canSearch, user?.id, minStatus]);

  const results = useMemo(() => {
    if (!isActive || !canSearch) return [];
    // Both skill tiers and instruments are filtered client-side so filter
    // changes are instant (no refetch).
    return matchedCourses.filter(
      (course) =>
        courseMatchesInstruments(course, instrumentPick) &&
        courseMatchesSkillTiers(course, tierPick),
    );
  }, [isActive, canSearch, matchedCourses, instrumentPick, tierPick]);

  const handleChangeText = useCallback(
    (text: string) => {
      setQuery(text);
      onQueryChange?.(text);
    },
    [onQueryChange],
  );

  useEffect(() => {
    if (!isActive) {
      onResultsChange?.(null);
      return;
    }
    if (!canSearch) {
      onResultsChange?.({ query: trimmedQuery, results: [] });
      return;
    }
    if (isSearching) return;
    onResultsChange?.({ query: trimmedQuery, results });
  }, [
    isActive,
    canSearch,
    trimmedQuery,
    results,
    isSearching,
    onResultsChange,
  ]);

  const searchField = (
    <SearchInputField
      isMobile={isMobile}
      value={query}
      onChangeText={handleChangeText}
      placeholder={isMobile ? "Search courses" : "Search for courses"}
      autoFocus={autoFocus}
      showClearButton
      onClear={() => handleChangeText("")}
    />
  );

  return (
    <YStack w="100%">
      <SkillLevelFilterToolbar
        isMobile={isMobile}
        tierPick={tierPick}
        setTierPick={onTierPickChange}
        instrumentPick={instrumentPick}
        setInstrumentPick={onInstrumentPickChange}
        skillMenuOpen={skillMenuOpen}
        setSkillMenuOpen={setSkillMenuOpen}
        leading={searchField}
      >
        {null}
      </SkillLevelFilterToolbar>
    </YStack>
  );
});
